// client/src/utils/paymentOnReceipt.ts
//
// THE OWNER of "was this vendor invoice already paid when it arrived?" and the
// rules that make the answer usable (BUSINESS_RULES#payment-on-receipt, D49).
//
// Why it exists: the upload never asked. An invoice paid on delivery (cash, card,
// a cheque handed over with the goods) was billed as unpaid on the Accounts
// screen the moment someone pressed Sync, and Accounts then chased a supplier
// for money that had already gone. The answer is now taken at upload, required
// with no default, kept on the invoice (`VendorInvoice.paymentOnReceipt`) and
// applied when Confirm & sync raises the bill.
//
//   unpaid        nothing has been paid; the bill is owed in full
//   paid_in_full  the whole invoice was paid on receipt; the bill is raised
//                 already settled, with a payment voucher (date, method, reference)
//   part_paid     part was paid; `amount` says how much. The amount is typed on
//                 the review screen because the invoice has not been read at
//                 upload time, so `amount` may be null until then.
//
// Pure and I/O-free: "today" is passed in, never read from the clock, so the
// server (Trinidad business day) and the browser (its own calendar) each supply
// their own and neither side guesses the other's.
//
// MIRRORED FILE: src/utils/paymentOnReceipt.js is a line-for-line twin.
// CRA's ModuleScopePlugin forbids importing from src/, so the copy is the only
// option. Change one, change both — `mirroredUtilParity.test.js` fails if you
// do not.

import { toInvoiceDate } from './paymentTerms';
import { round2 } from './invoiceLineMath';

export const PAYMENT_ON_RECEIPT_STATUSES = Object.freeze(['unpaid', 'paid_in_full', 'part_paid'] as const);
export type PaymentOnReceiptStatus = (typeof PAYMENT_ON_RECEIPT_STATUSES)[number];

// The methods the Record Payment form offers. One list, so a payment recorded
// on receipt and one recorded later read the same on a voucher.
export const PAYMENT_METHODS = Object.freeze(['Cheque', 'Bank Transfer', 'Cash', 'Card', 'Other'] as const);

const REFERENCE_MAX = 120;

// THE OWNER of "the latest day a payment may be dated" (D67, amending D49's
// "never in the future"). A cheque is written when the goods arrive, but often
// nobody is in to sign it, so the supplier collects it days later and it is dated
// that day. Every payment date reads this: the answer at upload, a payment
// recorded in Accounts, and a payment run. `today` is the caller's: the Trinidad
// business day on the server, the browser's calendar day on the client.
export const PAYMENT_DATE_MAX_DAYS_AHEAD = 7;
export const PAYMENT_DATE_TOO_LATE_MESSAGE = `A payment can be dated at most ${PAYMENT_DATE_MAX_DAYS_AHEAD} days after today.`;

/** The latest `yyyy-mm-dd` a payment may be dated, given today's `yyyy-mm-dd`. */
export function latestPaymentDay(today: string): string | null {
  const date = toInvoiceDate(today);
  if (!date) return null;
  return new Date(date.getTime() + PAYMENT_DATE_MAX_DAYS_AHEAD * 86400000).toISOString().slice(0, 10);
}

/** Is the `yyyy-mm-dd` day `day` later than a payment may be dated? An unreadable `today` refuses. */
export function isPaymentDayTooLate(day: string, today: string): boolean {
  const latest = latestPaymentDay(today);
  return !latest || day > latest;
}

export interface PaymentOnReceipt {
  status: PaymentOnReceiptStatus;
  paidDate: string | null;
  method: string;
  reference: string;
  amount: number | null;
}

export interface PaymentOnReceiptInput {
  status?: string;
  paidDate?: string;
  method?: string;
  reference?: string;
  amount?: number | null;
}

export type PaymentOnReceiptParse =
  | { ok: true; value: PaymentOnReceipt | null }
  | { ok: false; code: string; message: string };

function fail(code: string, message: string): { ok: false; code: string; message: string } {
  return { ok: false, code, message };
}

/**
 * Read a payment answer as it arrives from a form.
 *
 * `input.amount` is a NUMBER or null: turning what a person typed into a number
 * is the caller's job (`toNum` on the server, the form on the client), so this
 * file needs no number parser of its own.
 *
 *   requireAnswer  a missing status is a refusal (upload), not "no answer yet"
 *   today          yyyy-mm-dd; a paid date after `latestPaymentDay(today)` is refused
 */
export function parsePaymentOnReceipt(
  input: PaymentOnReceiptInput | null | undefined,
  { requireAnswer = false, today = null }: { requireAnswer?: boolean; today?: string | null } = {}
): PaymentOnReceiptParse {
  const status = String(input?.status ?? '').trim();
  if (!status) {
    return requireAnswer
      ? fail(
          'PAYMENT_ANSWER_REQUIRED',
          'Say whether this invoice has been paid: Not paid yet, Paid in full or Part paid.'
        )
      : { ok: true, value: null };
  }
  if (!(PAYMENT_ON_RECEIPT_STATUSES as readonly string[]).includes(status)) {
    return fail('PAYMENT_ANSWER_INVALID', 'Choose Not paid yet, Paid in full or Part paid.');
  }
  if (status === 'unpaid') {
    return { ok: true, value: { status, paidDate: null, method: '', reference: '', amount: null } };
  }

  const rawDate = String(input?.paidDate ?? '').trim();
  if (!rawDate) return fail('PAID_DATE_REQUIRED', 'Enter the date the invoice was paid.');
  const date = toInvoiceDate(rawDate);
  if (!date) return fail('PAID_DATE_INVALID', 'The date paid is not a real date.');
  const paidDate = date.toISOString().slice(0, 10);
  if (today && isPaymentDayTooLate(paidDate, today)) {
    return fail('PAID_DATE_IN_FUTURE', PAYMENT_DATE_TOO_LATE_MESSAGE);
  }

  const method = String(input?.method ?? '').trim();
  if (!method) return fail('PAID_METHOD_REQUIRED', 'Choose how the invoice was paid.');
  if (!(PAYMENT_METHODS as readonly string[]).includes(method)) {
    return fail('PAID_METHOD_INVALID', `Choose one of: ${PAYMENT_METHODS.join(', ')}.`);
  }

  const reference = String(input?.reference ?? '').trim();
  if (reference.length > REFERENCE_MAX) {
    return fail('PAID_REFERENCE_TOO_LONG', `The payment reference can be at most ${REFERENCE_MAX} characters.`);
  }

  let amount: number | null = null;
  if (status === 'part_paid' && input?.amount !== null && input?.amount !== undefined) {
    if (typeof input.amount !== 'number' || !Number.isFinite(input.amount) || !(input.amount > 0)) {
      return fail('PART_PAID_AMOUNT_INVALID', 'The amount already paid must be a number greater than zero.');
    }
    amount = round2(input.amount);
  }

  return {
    ok: true,
    value: { status: status as PaymentOnReceiptStatus, paidDate, method, reference, amount },
  };
}

/**
 * What still stops an invoice with this answer from being confirmed, or null.
 * Confirm & sync asks it, so an invoice uploaded before the question existed
 * (no answer) and a part payment with no amount typed yet both stop there.
 *
 * `grandTotal` is the invoice's total, when it is known.
 */
export function paymentOnReceiptBlocker(
  answer: Partial<PaymentOnReceipt> | null | undefined,
  grandTotal: number | null
): { code: string; message: string } | null {
  if (!answer || !(PAYMENT_ON_RECEIPT_STATUSES as readonly string[]).includes(String(answer.status))) {
    return {
      code: 'PAYMENT_ANSWER_REQUIRED',
      message:
        'Say whether this invoice has been paid (Not paid yet, Paid in full or Part paid) before it is confirmed. ' +
        'It decides whether the supplier is owed the money.',
    };
  }
  if (answer.status === 'part_paid') {
    if (!(typeof answer.amount === 'number' && answer.amount > 0)) {
      return {
        code: 'PART_PAID_AMOUNT_REQUIRED',
        message: 'This invoice is marked Part paid. Enter the amount already paid before it is confirmed.',
      };
    }
    if (typeof grandTotal === 'number' && grandTotal > 0 && answer.amount >= grandTotal) {
      return {
        code: 'PART_PAID_AMOUNT_TOO_LARGE',
        message:
          'The amount already paid is not less than the invoice total. Choose Paid in full, or correct the amount.',
      };
    }
  }
  return null;
}

/** The reference on the voucher: what was typed, else the invoice it was paid against. */
export function receiptPaymentReference(
  answer: Partial<PaymentOnReceipt> | null | undefined,
  invoiceNumber: string | null | undefined
): string {
  const typed = String(answer?.reference ?? '').trim();
  if (typed) return typed;
  const number = String(invoiceNumber ?? '').trim();
  return number ? `Paid on receipt - ${number}` : 'Paid on receipt';
}
